import type { ReactNode } from "react";
import { Col, Row, Table } from "react-bootstrap";
import type {
  FastTrackRefFundResponse,
  FastTrackShareClass,
} from "../../../api/src/fasttrack/refs.service";
import type {
  BondDescriptionResponse,
  BondPriceResponse,
} from "../../../api/src/fasttrack/bonds.service";
import type {
  ChartPeriodKey,
  PeriodStatistics,
} from "../../../api/src/security-details/market-statistics";
import type {
  MarketDataPart,
  MarketDataShareClassHeld,
  MarketDataStats,
  SecurityDetails as SecurityDetailsData,
  SecurityMarketData,
  SecurityMarketDataBond,
  SecurityMarketDataEquity,
} from "../../../api/src/security-details/security-details.types";
import { formatCurrency } from "../lib/numbers";
import { bondCashFlows, parseBondTermDate } from "./bondCashFlows";
import { isEmptyValue, NOT_AVAILABLE } from "./fastTrackValues";
import { ShareClassFlags, shareClassFlags } from "./ShareClassFlags";

export { isEmptyValue, NOT_AVAILABLE };

// APP-1487 (doc-security-market-data-01a150c533210e70): the FastTrack data of
// the security page. Each value shows the fault text of its own call, so one
// failed call or empty value leaves the rest of the page (R15, R40, R41).

/** The text for each value of a failed call (R41). */
export const DID_NOT_ANSWER =
  "FastTrack did not answer. Reload the page to try again.";

/** A return with one decimal, always shown: 0.1 is "10.0%" (R17, R25). */
export function returnText(value: number): string {
  const percent = Math.round(value * 1000) / 10;
  return `${(Object.is(percent, -0) ? 0 : percent).toFixed(1)}%`;
}

/** The text of the drawdown panel when no price after the valley reaches the peak (R26). */
export const NOT_RECOVERED = "Not recovered";

/** The text under the risk panel (R57). */
export const RISK_FREE_RATE_TEXT = "Risk-free rate 0%";

/** The market data query, as the page passes it to its panels. */
export type MarketDataQuery = {
  data: SecurityMarketData | undefined;
  isLoading: boolean;
  isError: boolean;
};

/** A FastTrack string-encoded number, or null for an empty value. */
export const numberOf = (value: unknown): number | null => {
  if (isEmptyValue(value)) return null;
  const number = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(number) ? number : null;
};

/** "as of MM/DD/YYYY" from the API's `YYYY-MM-DD`; null for no date (R38, R39). */
export function dataDateText(date: string | null | undefined): string | null {
  const match = date?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `as of ${match[2]}/${match[3]}/${match[1]}` : null;
}

/** The data of a part, or null when the call failed or the variant sends none. */
const dataOf = <T,>(part: MarketDataPart<T> | null | undefined) =>
  part?.status === "ok" ? part.data : null;

/**
 * The value of a call: a fault text when the call failed or the whole route
 * did not answer, `NOT_AVAILABLE` for an empty value, else `format(value)`.
 */
function valueText<T>(
  failed: boolean,
  value: T | null | undefined,
  format: (value: T) => string,
): string {
  if (failed) return DID_NOT_ANSWER;
  if (isEmptyValue(value)) return NOT_AVAILABLE;
  return format(value as T);
}

const asSent = (value: unknown) => String(value).trim();

export const DataDate = ({ date }: { date: string | null | undefined }) => {
  const text = dataDateText(date);
  return text ? <div className="small text-muted">{text}</div> : null;
};

/**
 * The page header price (R13, R14, R15, R41) with its data date (R38): for a
 * bond the price of the newest `/bonds/<CUSIP>/data` entry, for every other
 * variant `describe.price` of `/stats` in US dollars.
 */
export const HeaderPrice = ({
  marketData,
}: {
  marketData: MarketDataQuery;
}) => {
  const { data, isLoading, isError } = marketData;
  let value: string;
  let date: string | null = null;
  if (isLoading) {
    value = "Loading…";
  } else if (isError || !data) {
    value = DID_NOT_ANSWER;
  } else if (data.variant === "bond") {
    const failed = data.pricing.status !== "ok";
    value = valueText(failed, data.price, (price) => price.toFixed(2));
    date = failed ? null : data.dataDate;
  } else {
    const failed = data.stats.status !== "ok";
    value = valueText(failed, data.price, (price) => formatCurrency(price, 2));
    date = failed ? null : data.dataDate;
  }
  return (
    <div className="me-3 mb-3" data-testid="header-price">
      <div className="small text-muted">Price</div>
      <div className="fs-5 fw-semibold">{value}</div>
      <DataDate date={date} />
    </div>
  );
};

export const Panel = ({
  title,
  date,
  period,
  footer,
  children,
}: {
  title: string;
  date: string | null | undefined;
  /** The chart period the panel shows, for example "3Y" (R21). */
  period?: ChartPeriodKey | null;
  footer?: ReactNode;
  children: ReactNode;
}) => (
  <section aria-label={title}>
    <h3 className="fs-5 fw-semibold mb-1">
      {title}
      {period && (
        <span className="ms-2 badge text-bg-secondary" data-testid="period">
          {period}
        </span>
      )}
    </h3>
    <DataDate date={date} />
    <dl className="mt-2">{children}</dl>
    {footer}
  </section>
);

export const Item = ({ label, value }: { label: string; value: string }) => (
  <Row>
    <Col as="dt" xxl={5} sm={6}>
      {label}
    </Col>
    <Col as="dd">{value}</Col>
  </Row>
);

const RETURNS: {
  label: string;
  value: (stats: MarketDataStats) => unknown;
}[] = [
  { label: "1M", value: (stats) => stats.returns?.total?.onemonth },
  { label: "6M", value: (stats) => stats.returns?.total?.sixmonths },
  { label: "YTD", value: (stats) => stats.returns?.total?.ytd },
  { label: "1Y", value: (stats) => stats.returns?.total?.one },
  { label: "3Y", value: (stats) => stats.returns?.annualized?.three },
  { label: "5Y", value: (stats) => stats.returns?.annualized?.five },
  { label: "10Y", value: (stats) => stats.returns?.annualized?.ten },
];

const statsOf = (data: SecurityMarketDataEquity | undefined) =>
  dataOf(data?.stats);

/**
 * The returns panel (R20): the 1M, 6M, YTD and 1Y total returns and the 3Y,
 * 5Y and 10Y annualized returns, as the percent strings FastTrack sends.
 */
const ReturnsPanel = ({
  data,
  failed,
}: {
  data: SecurityMarketDataEquity | undefined;
  failed: boolean;
}) => {
  const stats = statsOf(data);
  return (
    <Panel title="Returns" date={failed ? null : data?.dataDate}>
      {RETURNS.map(({ label, value }) => (
        <Item
          key={label}
          label={label}
          value={valueText(failed, stats ? value(stats) : null, asSent)}
        />
      ))}
    </Panel>
  );
};

/** A string-encoded number of FastTrack with `format`, or `NOT_AVAILABLE`. */
const numberText = (value: unknown, format: (value: number) => string) => {
  const number = numberOf(value);
  return number === null ? NOT_AVAILABLE : format(number);
};

/**
 * The security data panel of a stock (R27): the exchange of `/ref/<symbol>/
 * details`, and the market cap, shares outstanding, yield and 52-week range of
 * `/stats`. Each value shows the fault of its own call.
 */
const SecurityDataPanel = ({
  data,
  failed,
}: {
  data: SecurityMarketDataEquity | undefined;
  failed: boolean;
}) => {
  const stats = statsOf(data);
  const describe = stats?.describe;
  const ref = dataOf(data?.ref);
  const refFailed = failed || !ref;
  const statsFailed = failed || !stats;
  const exchange = ref && "exchange" in ref ? ref.exchange : null;
  const usd = (value: number) => formatCurrency(value, 2);
  const range =
    isEmptyValue(describe?.low_52) && isEmptyValue(describe?.high_52)
      ? null
      : `${numberText(describe?.low_52, usd)} – ${numberText(describe?.high_52, usd)}`;
  return (
    <Panel title="Security data" date={statsFailed ? null : data?.dataDate}>
      <Item label="Exchange" value={valueText(refFailed, exchange, asSent)} />
      <Item
        label="Market cap"
        value={valueText(statsFailed, describe?.marketcap, (value) =>
          numberText(value, (number) => formatCurrency(number)),
        )}
      />
      <Item
        label="Shares outstanding"
        value={valueText(statsFailed, describe?.sharesout, (value) =>
          numberText(value, (number) => number.toLocaleString("en-US")),
        )}
      />
      <Item
        label="Yield"
        value={valueText(statsFailed, describe?.yield, asSent)}
      />
      <Item
        label="52-week range"
        value={valueText(statsFailed, range, (value) => value)}
      />
    </Panel>
  );
};

/** A market date of the API (a UTC midnight) as MM/DD/YYYY. */
const dayText = (date: Date) => {
  const [year, month, day] = date.toISOString().slice(0, 10).split("-");
  return `${month}/${day}/${year}`;
};

const ratioText = (value: number) => value.toFixed(2);

/** The statistics of the period the chart shows, or null when it is disabled or missing. */
const periodOf = (
  data: SecurityMarketDataEquity | undefined,
  period: ChartPeriodKey | null,
): PeriodStatistics | null =>
  data?.periods?.find((p) => p.period === period && !p.disabled) ?? null;

/**
 * The risk panel (R21, R57): the six risk statistics the API calculates for
 * the selected chart period, with the period label and the risk-free rate.
 * A failed `SP-DA` call shows the failed-call text for the beta, correlation
 * and R-squared, and the other three values still show (R44).
 */
const RiskPanel = ({
  data,
  failed,
  period,
}: {
  data: SecurityMarketDataEquity | undefined;
  failed: boolean;
  period: ChartPeriodKey | null;
}) => {
  const risk = periodOf(data, period)?.risk;
  const benchmarkFailed = failed || data?.benchmark.status !== "ok";
  return (
    <Panel
      title="Risk"
      date={failed ? null : data?.dataDate}
      period={failed ? null : period}
      footer={<div className="small text-muted">{RISK_FREE_RATE_TEXT}</div>}
    >
      <Item
        label="Standard deviation"
        value={valueText(failed, risk?.standardDeviation, returnText)}
      />
      <Item
        label="Sharpe ratio"
        value={valueText(failed, risk?.sharpeRatio, ratioText)}
      />
      <Item
        label="Sortino ratio"
        value={valueText(failed, risk?.sortinoRatio, ratioText)}
      />
      <Item
        label="Beta"
        value={valueText(benchmarkFailed, risk?.beta, ratioText)}
      />
      <Item
        label="Correlation"
        value={valueText(benchmarkFailed, risk?.correlation, ratioText)}
      />
      <Item
        label="R-squared"
        value={valueText(benchmarkFailed, risk?.rSquared, ratioText)}
      />
    </Panel>
  );
};

/**
 * The drawdown panel (R25, R26): the largest fall of the selected chart
 * period from a peak to a later valley, and the first date after the valley
 * with a price at or above the peak, else "Not recovered".
 */
const DrawdownPanel = ({
  data,
  failed,
  period,
}: {
  data: SecurityMarketDataEquity | undefined;
  failed: boolean;
  period: ChartPeriodKey | null;
}) => {
  const statistics = periodOf(data, period);
  const drawdown = statistics?.drawdown;
  const usd = (value: number) => formatCurrency(value, 2);
  return (
    <Panel
      title="Drawdown"
      date={failed ? null : data?.dataDate}
      period={failed ? null : period}
    >
      {!failed && statistics && !drawdown ? (
        <div>No price in the period fell below an earlier price.</div>
      ) : (
        <>
          <Item
            label="Peak"
            value={valueText(
              failed,
              drawdown,
              ({ peakDate, peakPrice }) =>
                `${dayText(peakDate)} at ${usd(peakPrice)}`,
            )}
          />
          <Item
            label="Valley"
            value={valueText(
              failed,
              drawdown,
              ({ valleyDate, valleyPrice }) =>
                `${dayText(valleyDate)} at ${usd(valleyPrice)}`,
            )}
          />
          <Item
            label="Decline"
            value={valueText(failed, drawdown?.decline, returnText)}
          />
          <Item
            label="Recovery"
            value={valueText(failed, drawdown, ({ recoveryDate }) =>
              recoveryDate ? dayText(recoveryDate) : NOT_RECOVERED,
            )}
          />
        </>
      )}
    </Panel>
  );
};

/** The variants that show the fund profile (R28, R109) and the Positions tab (R47). */
export const FUND_VARIANTS: SecurityDetailsData["security"]["variant"][] = [
  "fund",
  "bond_fund",
  "money_market",
];

/** The text of the "Held" column for a class the firm does not hold (R97). */
export const NOT_HELD = "—";

/** A FastTrack fraction string as a percent with 2 decimals: "0.000300" is "0.03%". */
const fractionText = (value: unknown) =>
  numberText(value, (number) => `${(number * 100).toFixed(2)}%`);

/** A FastTrack percent string with 2 decimals: "2.00" is "2.00%". */
const percentText = (value: unknown) =>
  numberText(value, (number) => `${number.toFixed(2)}%`);

/** A FastTrack string of US dollars in whole dollars: "500000" is "$500,000". */
const dollarsText = (value: unknown) =>
  numberText(value, (number) => formatCurrency(number));

/** `open2new` "yes" or "no" as "Yes" or "No"; any other value as sent. */
const yesNoText = (value: unknown) => {
  const text = asSent(value);
  if (text.toLowerCase() === "yes") return "Yes";
  if (text.toLowerCase() === "no") return "No";
  return text;
};

/** The fund fields of `/ref/<symbol>/details`; a stock answer has none of them. */
const fundRefOf = (
  data: SecurityMarketDataEquity | undefined,
): Partial<FastTrackRefFundResponse> | null => {
  const ref = dataOf(data?.ref);
  if (!ref) return null;
  return "security_type" in ref && ref.security_type !== "Stock" ? ref : {};
};

/**
 * The "Share classes" list of a mutual fund (R29, R95 to R97): each class of
 * `/ref/<symbol>/shareclasses` in its order, with the market value of the
 * firm's held positions in it. A failed call shows the failed-call text in
 * the list (R41). The list starts no swap.
 */
const ShareClassesList = ({
  data,
  failed,
}: {
  data: SecurityMarketDataEquity | undefined;
  failed: boolean;
}) => {
  const answer = failed ? null : dataOf(data?.shareClasses);
  const classes: FastTrackShareClass[] = answer?.datalist ?? [];
  const held = new Map<string, MarketDataShareClassHeld["held"]>(
    (data?.shareClassesHeld ?? []).map((entry) => [entry.ticker, entry.held]),
  );
  const heldText = (ticker: string) => {
    const value = held.get(ticker)?.marketValue;
    return value === undefined ? NOT_HELD : formatCurrency(Math.round(value));
  };
  return (
    <section aria-label="Share classes" className="mt-3">
      <h4 className="fs-6 fw-semibold">Share classes</h4>
      {!answer ? (
        <div>{DID_NOT_ANSWER}</div>
      ) : classes.length === 0 ? (
        <div>{NOT_AVAILABLE}</div>
      ) : (
        <Table size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Ticker</th>
              <th>Class</th>
              <th className="text-end">Expense</th>
              <th className="text-end">12b-1</th>
              <th className="text-end">Deferred load</th>
              <th className="text-end">Minimum initial</th>
              <th className="text-end">Held</th>
            </tr>
          </thead>
          <tbody>
            {classes.map((shareClass, i) => (
              <tr key={`${shareClass.ticker}-${i}`}>
                <td>{valueText(false, shareClass.ticker, asSent)}</td>
                <td>{valueText(false, shareClass.shareclass, asSent)}</td>
                <td className="text-end">
                  {fractionText(shareClass.expense_ratio)}
                </td>
                {/* The unit of the 12b-1 fee and the deferred load is
                    believed to be a fraction, as `expense_ratio` (A9). */}
                <td className="text-end">
                  {fractionText(shareClass.expense_12b)}
                </td>
                <td className="text-end">
                  {fractionText(shareClass.expense_deferred)}
                </td>
                <td className="text-end">
                  {dollarsText(shareClass.initial_purchase)}
                </td>
                <td className="text-end">{heldText(shareClass.ticker)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
};

/**
 * The fund profile of a fund variant (R28, R109) from `/ref/<symbol>/details`,
 * with no data date (R39), and for a mutual fund the "Share classes" list
 * (R29, R95) with the "Flags" list below it (R98 to R108). A failed `/ref`
 * call shows the failed-call text for each value of the profile; the list
 * shows the fault of its own call (R41).
 */
const FundProfilePanel = ({
  data,
  failed,
  isMutualFund,
}: {
  data: SecurityMarketDataEquity | undefined;
  failed: boolean;
  isMutualFund: boolean;
}) => {
  const ref = failed ? null : fundRefOf(data);
  const refFailed = !ref;
  const detail = ref?.expense_detail;
  const item = (
    label: string,
    value: unknown,
    format: (value: unknown) => string,
  ) => <Item label={label} value={valueText(refFailed, value, format)} />;
  return (
    <Panel
      title="Fund profile"
      date={null}
      footer={
        isMutualFund && (
          <>
            <ShareClassesList data={data} failed={failed} />
            <ShareClassFlags
              flags={
                failed || !data
                  ? []
                  : shareClassFlags({
                      symbol: data.symbol,
                      securityType: "Mutual Fund",
                      shareClasses: data.shareClasses,
                      shareClassesHeld: data.shareClassesHeld,
                    })
              }
            />
          </>
        )
      }
    >
      {item("Family", ref?.family, asSent)}
      {item("Category", ref?.category, asSent)}
      {item("Objective", ref?.objective, asSent)}
      {item("Share class", ref?.shareclass, asSent)}
      {item("Expense ratio", ref?.expenseratio, fractionText)}
      {item(
        "Prospectus expense ratio",
        detail?.expense_prospectus,
        fractionText,
      )}
      {item("Front load", detail?.frontload, percentText)}
      {item("Deferred load", detail?.deferred, percentText)}
      {item("Redemption fee", detail?.redemption, percentText)}
      {item("12b-1 fee", detail?.b12, percentText)}
      {item("Minimum initial purchase", detail?.initpurchase, dollarsText)}
      {item("Open to new investors", detail?.open2new, yesNoText)}
      {item("Turnover", ref?.turnover, percentText)}
    </Panel>
  );
};

/** A bond term date as MM/DD/YYYY, or as sent when it does not parse (R30). */
const termDateText = (value: unknown) => {
  const date = parseBondTermDate(value);
  return date ? dayText(date) : asSent(value);
};

/** A FastTrack flag "Y", "yes" or "true" as "Yes", the opposite as "No"; any other value as sent (R30). */
const flagText = (value: unknown) => {
  const text = asSent(value).toLowerCase();
  if (["y", "yes", "true", "1"].includes(text)) return "Yes";
  if (["n", "no", "false", "0"].includes(text)) return "No";
  return asSent(value);
};

/**
 * The bond terms panel (R30) from `/bonds/<CUSIP>/description`: the coupon
 * rate and type, the payment frequency, the dated and first coupon dates,
 * the maturity date, the day count, and the callable and putable flags. The
 * panel shows the data date of the bond panels (R38, D12).
 */
const BondTermsPanel = ({
  data,
  failed,
}: {
  data: SecurityMarketDataBond | undefined;
  failed: boolean;
}) => {
  const answer: Partial<BondDescriptionResponse> | null = failed
    ? null
    : dataOf(data?.description);
  const termsFailed = !answer;
  const item = (
    label: string,
    value: unknown,
    format: (value: unknown) => string,
  ) => <Item label={label} value={valueText(termsFailed, value, format)} />;
  const couponType = isEmptyValue(answer?.issuer?.coupon_type)
    ? answer?.issuer?.coupon_type_other
    : answer?.issuer?.coupon_type;
  return (
    <Panel title="Bond terms" date={termsFailed ? null : data?.dataDate}>
      {item("Coupon rate", answer?.details?.rate, percentText)}
      {item("Coupon type", couponType, asSent)}
      {item("Payment frequency", answer?.details?.payment_frequency, asSent)}
      {item("Dated date", answer?.issuer?.dated_date, termDateText)}
      {item("First coupon date", answer?.issuer?.coupon_first, termDateText)}
      {item("Maturity date", answer?.details?.date_maturity, termDateText)}
      {item("Day count", answer?.issuer?.coupon_day_count, asSent)}
      {item("Callable", answer?.redemptive_items?.callable, flagText)}
      {item("Putable", answer?.redemptive_items?.putable, flagText)}
    </Panel>
  );
};

/** A FastTrack number with 2 decimals: a duration in years, or the convexity. */
const decimalText = (value: unknown) => numberText(value, ratioText);

/** A FastTrack spread in basis points: "45.3" is "45.30 bp". */
const basisPointsText = (value: unknown) =>
  numberText(value, (number) => `${number.toFixed(2)} bp`);

/**
 * The bond analytics panel (R31): the mid yields, durations, convexity and
 * spreads of the newest `/bonds/<CUSIP>/data` entry by `date`, which the API
 * picks out as `newest` (never `prices[0]`: the early entries are "NA").
 */
const BondAnalyticsPanel = ({
  data,
  failed,
}: {
  data: SecurityMarketDataBond | undefined;
  failed: boolean;
}) => {
  const pricing = failed ? null : dataOf(data?.pricing);
  const newest: Partial<BondPriceResponse> | null = pricing?.newest ?? null;
  const analyticsFailed = !pricing;
  const item = (
    label: string,
    value: unknown,
    format: (value: unknown) => string,
  ) => <Item label={label} value={valueText(analyticsFailed, value, format)} />;
  return (
    <Panel
      title="Bond analytics"
      date={analyticsFailed ? null : data?.dataDate}
    >
      {item("Yield to maturity", newest?.yield?.ytm?.mid, percentText)}
      {item("Current yield", newest?.yield?.current?.mid, percentText)}
      {item("Modified duration", newest?.duration?.modified_mid, decimalText)}
      {item("Effective duration", newest?.duration?.effective_mid, decimalText)}
      {item("Macaulay duration", newest?.duration?.macaulay_mid, decimalText)}
      {item("Convexity", newest?.convexity?.mid, decimalText)}
      {item(
        "Spread to Treasury",
        newest?.spreads?.oas_treasury?.mid,
        basisPointsText,
      )}
      {item("Z-spread", newest?.spreads?.z?.mid, basisPointsText)}
    </Panel>
  );
};

/** Today as a UTC midnight in America/New_York, the as-of date when no entry has a date. */
const todayInNewYork = () =>
  new Date(
    `${new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" })}T00:00:00.000Z`,
  );

/**
 * The cash flow panel (R32): each payment after the data date to maturity
 * for $100 of par, from the coupon terms of `/bonds/<CUSIP>/description`
 * (`bondCashFlows`). Terms that cannot give the schedule show
 * "Not available from FastTrack" (R40); a failed call shows the failed-call
 * text (R41).
 */
const BondCashFlowsPanel = ({
  data,
  failed,
}: {
  data: SecurityMarketDataBond | undefined;
  failed: boolean;
}) => {
  const answer: Partial<BondDescriptionResponse> | null = failed
    ? null
    : dataOf(data?.description);
  const asOf = parseBondTermDate(data?.dataDate) ?? todayInNewYork();
  const flows = answer
    ? bondCashFlows({
        rate: answer.details?.rate,
        paymentFrequency: answer.details?.payment_frequency,
        maturity: answer.details?.date_maturity,
        firstCoupon: answer.issuer?.coupon_first,
        asOf,
      })
    : null;
  return (
    <section aria-label="Cash flows">
      <h3 className="fs-5 fw-semibold mb-1">Cash flows</h3>
      <DataDate date={answer ? data?.dataDate : null} />
      <div className="small text-muted">For $100 of par</div>
      {!answer ? (
        <div className="mt-2">{DID_NOT_ANSWER}</div>
      ) : !flows ? (
        <div className="mt-2">{NOT_AVAILABLE}</div>
      ) : flows.length === 0 ? (
        <div className="mt-2">No payment is left before maturity.</div>
      ) : (
        <Table size="sm" className="mt-2 mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th className="text-end">Payment</th>
            </tr>
          </thead>
          <tbody>
            {flows.map(({ date, amount }) => (
              <tr key={date.toISOString()}>
                <td>{dayText(date)}</td>
                <td className="text-end">{formatCurrency(amount, 2)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
};

/**
 * The bond panels of the Overview tab (R30 to R32): the terms, analytics and
 * cash flows of an individual bond. Each shows the fault of its own call.
 */
const OverviewBondData = ({ marketData }: { marketData: MarketDataQuery }) => {
  const data =
    marketData.data?.variant === "bond" ? marketData.data : undefined;
  // The route did not answer: each value of each call failed (R41).
  const failed = marketData.isError || !data;
  return (
    <>
      <Col lg={6}>
        <BondTermsPanel data={data} failed={failed} />
      </Col>
      <Col lg={6}>
        <BondAnalyticsPanel data={data} failed={failed} />
      </Col>
      <Col xs={12}>
        <BondCashFlowsPanel data={data} failed={failed} />
      </Col>
    </>
  );
};

/**
 * The market-data panels of the Overview tab, each in its own column: the
 * returns (R20), risk (R21) and drawdown (R25) of every variant but a bond
 * (R117), the security data of a stock (R27), and the fund profile of a
 * fund variant (R28) with the "Share classes" list of a mutual fund (R29),
 * and the bond terms, analytics and cash flows of a bond (R30 to R32).
 * The risk and drawdown
 * panels show the period the chart shows. The page's other sections show
 * while these load or fail.
 */
export const OverviewMarketData = ({
  variant,
  securityType,
  marketData,
  period,
}: {
  variant: SecurityDetailsData["security"]["variant"];
  securityType: string;
  marketData: MarketDataQuery;
  /** The chart period the advisor selected, or the one it opens on. */
  period: ChartPeriodKey | null;
}) => {
  if (marketData.isLoading) {
    return (
      <Col xs={12} className="text-muted">
        Loading FastTrack data…
      </Col>
    );
  }
  if (variant === "bond") return <OverviewBondData marketData={marketData} />;
  const data =
    marketData.data && marketData.data.variant !== "bond"
      ? marketData.data
      : undefined;
  // The route did not answer: each value of each call failed (R41).
  const failed = marketData.isError || !data;
  const statsFailed = failed || data?.stats.status !== "ok";
  return (
    <>
      <Col lg={6}>
        <ReturnsPanel data={data} failed={statsFailed} />
      </Col>
      <Col lg={6}>
        <RiskPanel data={data} failed={statsFailed} period={period} />
      </Col>
      <Col lg={6}>
        <DrawdownPanel data={data} failed={statsFailed} period={period} />
      </Col>
      {variant === "stock" && (
        <Col lg={6}>
          <SecurityDataPanel data={data} failed={failed} />
        </Col>
      )}
      {FUND_VARIANTS.includes(variant) && (
        <Col xs={12}>
          <FundProfilePanel
            data={data}
            failed={failed}
            isMutualFund={securityType === "Mutual Fund"}
          />
        </Col>
      )}
    </>
  );
};
